import { ReactNode, useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { useLanguage } from '@/context/LanguageContext';
import { useTheme } from '@/context/ThemeContext';
import { useTable } from '@/context/TableContext';
import { useAuth } from '@/context/AuthContext';
import { cn } from '@/utils';
import {
  Sun,
  Moon,
  Monitor,
  Globe,
  RefreshCw,
  Phone,
  BarChart3,
  Users,
  ChevronDown,
  LayoutDashboard,
  Table,
} from 'lucide-react';

type Page = 'records' | 'analytics' | 'admin';

interface HeaderProps {
  onRefresh: () => void;
  loading: boolean;
  connected: boolean;
  currentPage: Page;
  onPageChange: (page: Page) => void;
  isAdmin?: boolean;
  profile?: any;
  rightContent?: ReactNode;
}

// Available pages
const availablePages: Page[] = ['records', 'analytics'];

// Get icon for page
const getPageIcon = (page: Page) => {
  switch (page) {
    case 'records':
      return Phone;
    case 'analytics':
      return BarChart3;
    default:
      return LayoutDashboard;
  }
};

// Get translation key for page
const getPageTranslationKey = (page: Page): string => {
  switch (page) {
    case 'records':
      return 'nav.records';
    case 'analytics':
      return 'nav.analytics';
    case 'admin':
      return 'nav.admin';
    default:
      return 'nav.records';
  }
};

export function Header({
  onRefresh,
  loading,
  connected,
  currentPage,
  onPageChange,
  isAdmin = false,
  profile,
  rightContent,
}: HeaderProps) {
  const { language, setLanguage, t } = useLanguage();
  const { theme, setTheme, resolvedTheme } = useTheme();
  const { currentTable, setCurrentTable, availableTables, loadingTables } = useTable();
  const { user } = useAuth();
  const [showThemeMenu, setShowThemeMenu] = useState(false);
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [showTableMenu, setShowTableMenu] = useState(false);
  const themeMenuRef = useRef<HTMLDivElement>(null);
  const langMenuRef = useRef<HTMLDivElement>(null);
  const tableMenuRef = useRef<HTMLDivElement>(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (themeMenuRef.current && !themeMenuRef.current.contains(event.target as Node)) {
        setShowThemeMenu(false);
      }
      if (langMenuRef.current && !langMenuRef.current.contains(event.target as Node)) {
        setShowLangMenu(false);
      }
      if (tableMenuRef.current && !tableMenuRef.current.contains(event.target as Node)) {
        setShowTableMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleLanguage = () => {
    setLanguage(language === 'en' ? 'ar' : 'en');
    setShowLangMenu(false);
  };

  const themeOptions = [
    { value: 'light' as const, label: 'Light', icon: Sun },
    { value: 'dark' as const, label: 'Dark', icon: Moon },
    { value: 'system' as const, label: 'System', icon: Monitor },
  ];

  return (
    <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-lg border-b">
      <div className="max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo and Navigation */}
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                <Phone className="h-5 w-5 text-primary-foreground" />
              </div>
              <div className="hidden sm:block">
                <h1 className="text-lg font-bold">
                  {language === 'ar' 
                    ? (profile?.full_name_arabic || profile?.full_name || user?.email || 'مستخدم')
                    : (profile?.full_name || user?.email || 'User')
                  }
                </h1>
                <p className="text-xs text-muted-foreground">AI Receptionist Dashboard</p>
              </div>
            </div>

            {/* Navigation Tabs */}
            <nav className="hidden md:flex items-center gap-1">
              {availablePages.map((page) => {
                const Icon = getPageIcon(page);
                const translationKey = getPageTranslationKey(page);

                return (
                  <button
                    key={page}
                    onClick={() => onPageChange(page)}
                    className={cn(
                      'px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200',
                      currentPage === page
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    )}
                  >
                    <Icon className="w-4 h-4 inline mr-2" />
                    {t(translationKey) || page}
                  </button>
                );
              })}
              {isAdmin && (
                <button
                  onClick={() => onPageChange('admin')}
                  className={cn(
                    'px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200',
                    currentPage === 'admin'
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  )}
                >
                  <Users className="w-4 h-4 inline mr-2" />
                  {t('nav.admin')}
                </button>
              )}
            </nav>
          </div>



          {/* Actions */}
          <div className="flex items-center gap-2">
            {/* Table Selector - Only show for admins */}
            {isAdmin && (
            <div className="relative" ref={tableMenuRef}>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowTableMenu(!showTableMenu)}
                className="h-9 gap-2"
                disabled={loadingTables}
              >
                <Table className="h-4 w-4" />
                <span className="hidden sm:inline">
                  {loadingTables ? 'Loading...' : currentTable}
                </span>
                <ChevronDown className="h-3 w-3" />
              </Button>
              {showTableMenu && (
                <div className="absolute right-0 mt-2 w-56 bg-background border rounded-lg shadow-lg py-1 z-50 max-h-80 overflow-y-auto">
                  {loadingTables ? (
                    <div className="px-4 py-3 text-sm text-muted-foreground">
                      Loading tables...
                    </div>
                  ) : availableTables.length > 0 ? (
                    availableTables.map((table) => (
                      <button
                        key={table.name}
                        onClick={() => {
                          setCurrentTable(table.name);
                          setShowTableMenu(false);
                        }}
                        className={cn(
                          'w-full px-4 py-2 text-left text-sm hover:bg-muted flex items-center justify-between',
                          currentTable === table.name && 'bg-muted'
                        )}
                      >
                        <div className="flex flex-col">
                          <span className="font-medium">{table.label}</span>
                          <span className="text-xs text-muted-foreground">{table.name}</span>
                        </div>
                        {currentTable === table.name && (
                          <span className="text-primary text-xs">✓</span>
                        )}
                      </button>
                    ))
                  ) : (
                    <div className="px-4 py-3 text-sm text-muted-foreground">
                      No tables found
                    </div>
                  )}
                </div>
              )}
            </div>
            )}

            {/* Refresh Button */}
            <Button
              variant="outline"
              size="icon"
              onClick={onRefresh}
              disabled={loading}
              className="h-9 w-9"
            >
              <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} />
            </Button>

            {/* Language Toggle */}
            <div className="relative" ref={langMenuRef}>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowLangMenu(!showLangMenu)}
                className="h-9 gap-2"
              >
                <Globe className="h-4 w-4" />
                <span className="hidden sm:inline">{language.toUpperCase()}</span>
                <ChevronDown className="h-3 w-3" />
              </Button>
              {showLangMenu && (
                <div className="absolute right-0 mt-2 w-32 bg-background border rounded-lg shadow-lg py-1 z-50">
                  <button
                    onClick={toggleLanguage}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-muted"
                  >
                    {language === 'en' ? 'العربية' : 'English'}
                  </button>
                </div>
              )}
            </div>

            {/* Theme Toggle */}
            <div className="relative" ref={themeMenuRef}>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setShowThemeMenu(!showThemeMenu)}
                className="h-9 w-9"
              >
                {resolvedTheme === 'dark' ? (
                  <Moon className="h-4 w-4" />
                ) : (
                  <Sun className="h-4 w-4" />
                )}
              </Button>
              {showThemeMenu && (
                <div className="absolute right-0 mt-2 w-36 bg-background border rounded-lg shadow-lg py-1 z-50">
                  {themeOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => {
                        setTheme(option.value);
                        setShowThemeMenu(false);
                      }}
                      className={cn(
                        'w-full px-4 py-2 text-left text-sm hover:bg-muted flex items-center gap-2',
                        theme === option.value && 'bg-muted'
                      )}
                    >
                      <option.icon className="h-4 w-4" />
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Connection Status */}
            <Badge
              variant={connected ? 'success' : 'destructive'}
              dot
              className="hidden sm:flex"
            >
              {connected ? t('status.connected') : t('status.disconnected')}
            </Badge>

            {/* User Menu or Custom Right Content */}
            {rightContent}
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <div className="md:hidden border-t px-4 py-2 overflow-x-auto">
        <div className="flex gap-2">
          {availablePages.map((page) => {
            const Icon = getPageIcon(page);
            const translationKey = getPageTranslationKey(page);

            return (
              <button
                key={page}
                onClick={() => onPageChange(page)}
                className={cn(
                  'px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all',
                  currentPage === page
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted'
                )}
              >
                <Icon className="w-4 h-4 inline mr-1" />
                {t(translationKey) || page}
              </button>
            );
          })}
          {isAdmin && (
            <button
              onClick={() => onPageChange('admin')}
              className={cn(
                'px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all',
                currentPage === 'admin'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted'
              )}
            >
              <Users className="w-4 h-4 inline mr-1" />
              {t('nav.admin')}
            </button>
          )}
        </div>
      </div>
    </header>
  );
}
